"use client"

import { useEffect, useCallback, useState } from "react"
import { useDemoStore } from "@/store/demoStore"
import { demoApi } from "@/services/demoApi"
import { useDemoLimit } from "@/hooks/useDemoLimit"

import type { DemoMessage } from "@/store/demoStore"

const CONSENT_KEY = "demo-consent-given"

export interface UseDemoSocketReturn {
  isConnected: boolean
  isConnecting: boolean
  error: string | null
  sendMessage: (question: string) => void
  messages: DemoMessage[]
}

export const useDemoSocket = (): UseDemoSocketReturn => {
  const messages = useDemoStore((state) => state.messages)
  const isConnected = useDemoStore((state) => state.isConnected)
  const isConnecting = useDemoStore((state) => state.isConnecting)
  const error = useDemoStore((state) => state.error)
  const selectedPaths = useDemoStore((state) => state.selectedPaths)
  const getHistory = useDemoStore((state) => state.getHistory)
  const { visitorId } = useDemoLimit()

  // Track consent state to trigger re-renders
  const [hasConsent, setHasConsent] = useState<boolean>(() => {
    if (typeof window === "undefined") return false
    return localStorage.getItem(CONSENT_KEY) === "true"
  })

  // Check consent and update state
  const checkConsent = useCallback(() => {
    if (typeof window === "undefined") return false
    const consent = localStorage.getItem(CONSENT_KEY) === "true"
    // Only update state if it actually changed to avoid unnecessary re-renders
    setHasConsent((prev) => {
      if (prev !== consent) {
        return consent
      }
      return prev
    })
    return consent
  }, [])

  // Listen for consent changes
  useEffect(() => {
    // Check initial consent
    checkConsent()

    // Listen for storage events (from other tabs/windows)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CONSENT_KEY) {
        checkConsent()
      }
    }
    window.addEventListener("storage", handleStorageChange)

    // Poll for consent changes (for same-tab changes)
    const interval = setInterval(() => {
      checkConsent()
    }, 500) // Check every 500ms

    return () => {
      window.removeEventListener("storage", handleStorageChange)
      clearInterval(interval)
    }
  }, [checkConsent])

  useEffect(() => {
    // Check if visitorId is a fallback format (timestamp-random)
    const isFallbackFormat = visitorId ? /^\d+-\d+\.\d+$/.test(visitorId) : false
    
    // Only connect if consent is given AND visitorId is available AND it's NOT a fallback format
    // (If consent is given, we should wait for FingerprintJS to complete, which will replace the fallback)
    if (visitorId && hasConsent && !isFallbackFormat) {
      demoApi.connect(visitorId)
    } else {
      // Disconnect if consent is revoked, visitorId is removed, or we're waiting for FingerprintJS
      demoApi.disconnect()
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      // Only disconnect on unmount, not on dependency changes (to avoid disconnect/reconnect loops)
      // We'll let the next effect run handle the connection state
    }
  }, [visitorId, hasConsent])

  const sendMessage = (question: string) => {
    const history = getHistory()
    demoApi.sendMessage(question, selectedPaths, history)
  }

  return {
    isConnected,
    isConnecting,
    error,
    sendMessage,
    messages,
  }
}
